import {
  toWSDateString,
  WSCard,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSList,
  WSLoader,
  WSPill,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { WorkLogStatus } from "@wingspanhq/wingspan-api-sdk";
import { useHistory } from "react-router-dom";
import { getNextPayrollCutOffDate } from "../../../Invoices/utils/payables";
import { useQueryPayrollSettings } from "../../../query/payments/queries";
import { useQueryWorkLogsSummary } from "../../../query/workLog/queries/useQueryWorkLogsSummary";
import { useCurrentPayPeriod } from "../hooks/useCurrentPayPeriod";
import { routeHelpersWorkLogsPayerToApprove } from "../paths";
import { WorkLogsDashboardEmptyState } from "./WorkLogsEmptyStates";
import { WidgetWorkLogsPayerView } from "./WidgetWorkLogsPayerView";
import { useSession } from "@hooks/useSession";

export const WorklogsPayerDashboard: React.FC = () => {
  const history = useHistory();
  const { accountId } = useSession();

  // Query to check if any work logs exist
  // Payer scope filtered by `payerAccountId.eq` using the v3 session accountId.
  const queryHasAnyWorkLogs = useQueryWorkLogsSummary({
    filter: { payerAccountId: { eq: accountId } }
  });

  const hasAnyWorkLogs = (queryHasAnyWorkLogs.data?.listSize ?? 0) > 0;

  // Query for "To approve" count
  const queryToApproveCount = useQueryWorkLogsSummary({
    filter: {
      payerAccountId: { eq: accountId },
      status: { anyOf: [WorkLogStatus.IN_REVIEW] }
    }
  });

  const toApproveCount = queryToApproveCount.data?.listSize ?? 0;

  // Query for payroll settings to get next payroll date
  const queryPayrollSettings = useQueryPayrollSettings(accountId, {
    retry: false
  });
  const nextPayrollDate = queryPayrollSettings.data
    ? toWSDateString(
        getNextPayrollCutOffDate(queryPayrollSettings.data),
        "monthDate"
      )
    : "--";

  // Get current and next pay period info from the v3 pay periods endpoint
  const { currentPayPeriod, nextPayPeriod } = useCurrentPayPeriod();

  const currentPayPeriodText = currentPayPeriod
    ? `${toWSDateString(currentPayPeriod.startDate, "monthDayYear")} - ${toWSDateString(currentPayPeriod.endDate, "monthDayYear")}`
    : undefined;

  // Show spinner while loading
  if (queryHasAnyWorkLogs.isLoading) {
    return <WSLoader.Spinner my="2XL" size="S" color="gray500" />;
  }

  // If no work logs exist, show empty state
  if (!hasAnyWorkLogs) {
    return <WorkLogsDashboardEmptyState />;
  }

  return (
    <WSGrid gutter="2XL" mt="XL">
      <WSGrid.Item span={{ m: "8" }}>
        <WSList gap="XL">
          {/* Top-level "To approve" card */}
          <WSCard
            divider="bottom"
            header={{
              label: {
                avatar: {
                  type: "icon",
                  icon: "alert",
                  size: "M",
                  colorBackground: "amber50",
                  color: "amber400"
                },
                text: "To approve:"
              }
            }}
            actions={{
              alignment: "left",
              buttons: [
                {
                  kind: "Link",
                  label: "Review and approve",
                  rightIcon: "chevron-right",
                  size: "S",
                  onClick: () =>
                    history.push(routeHelpersWorkLogsPayerToApprove.path)
                }
              ]
            }}
          >
            <WSText.ParagraphSm color="gray500">
              {toApproveCount} work logs awaiting your review
            </WSText.ParagraphSm>
          </WSCard>

          {/* Next pay period section */}
          <WSElement>
            <WSFlexBox justify="space-between" alignItems="center" mb="M">
              <WSText.Heading5>Next pay period</WSText.Heading5>
              {nextPayPeriod && (
                <WSPill
                  text={`${nextPayPeriod.daysLeftToOpen} days left to open`}
                  theme="blue"
                  icon="info-circle"
                />
              )}
            </WSFlexBox>

            <WSCard
              divider="bottom"
              header={{
                label: {
                  avatar: {
                    type: "icon",
                    icon: "calendar",
                    size: "M",
                    colorBackground: "blue50",
                    color: "blue500"
                  },
                  text: `Next payroll: ${nextPayrollDate}`
                }
              }}
              actions={{
                alignment: "left",
                buttons: [
                  {
                    kind: "Link",
                    label: "View in payables",
                    rightIcon: "chevron-right",
                    size: "S",
                    onClick: () => history.push("/member/invoices/payables")
                  }
                ]
              }}
            >
              <WSText.ParagraphSm color="gray500">
                This is your next scheduled payment cycle
              </WSText.ParagraphSm>
            </WSCard>
          </WSElement>

          {/* Current pay period section */}
          <WSElement>
            <WSFlexBox justify="space-between" alignItems="center" mb="M">
              <WSText.Heading5>Current pay period</WSText.Heading5>
              {currentPayPeriod && (
                <WSPill
                  text={`${currentPayPeriod.daysLeftToReview} days left to review`}
                  theme="warning"
                  icon="alert"
                />
              )}
            </WSFlexBox>

            <WSCard
              divider="bottom"
              header={{
                label: {
                  avatar: {
                    type: "icon",
                    icon: "alert",
                    size: "M",
                    colorBackground: "amber50",
                    color: "amber400"
                  },
                  text: "To approve:"
                }
              }}
              actions={{
                alignment: "left",
                buttons: [
                  {
                    kind: "Link",
                    label: "Review and approve",
                    rightIcon: "chevron-right",
                    size: "S",
                    onClick: () =>
                      history.push(routeHelpersWorkLogsPayerToApprove.path)
                  }
                ]
              }}
            >
              <WSText.ParagraphSm color="gray500">
                {toApproveCount} work logs awaiting your review
              </WSText.ParagraphSm>
            </WSCard>
          </WSElement>
        </WSList>
      </WSGrid.Item>

      <WSGrid.Item span={{ m: "4" }}>
        <WSList gap="XL">
          {/* Current pay period card */}
          {currentPayPeriodText && (
            <WSElement>
              <WSSectionToolbar title="Current pay period" />
              <WSCard>
                <WSText.ParagraphXs color="gray500">
                  Pay period
                </WSText.ParagraphXs>
                <WSText.ParagraphSm color="gray700" mt="XS">
                  {currentPayPeriodText}
                </WSText.ParagraphSm>
              </WSCard>
            </WSElement>
          )}

          <WidgetWorkLogsPayerView
            title="Recent work logs"
            showEngagementDetails
            hideViewAll
          />
        </WSList>
      </WSGrid.Item>
    </WSGrid>
  );
};
